import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Theme,
} from '@material-ui/core';
import { useMutation } from 'react-apollo';

import { Form, Field, TextField, useModal, SelectField } from 'src/app-builder';
import { useNotification } from 'src/hooks';
import { SubmitButton } from 'src/components';
import { EVENT_RESOLVER_MUTATION, EventType } from 'src/graphql';
import { COMPANY_TEAMMATE_APP_ROLE_NAMES_LIST, APP_ROLES } from 'src/types';
import { CompanyTeammateInvitationSendEventSchema } from 'src/utils/schemas/event';
import { validateDataByYup, isFunction, commonErrorResolver, t } from 'src/utils';
import { DIALOGS } from './constants';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: theme.spacing(80),
  },
}));

interface TeamInviteDialogProps {}

export const TeamInviteDialog: React.FC<TeamInviteDialogProps> = () => {
  const classes = useStyles();

  const notification = useNotification();

  const { open, closeModal, args, openModal } = useModal(DIALOGS.TEAM_INVITE_DIALOG);

  const roleOptions = React.useMemo(
    () =>
      COMPANY_TEAMMATE_APP_ROLE_NAMES_LIST.map(name => {
        const isRoleToDrop =
          !args || (args?.doesCompanyHaveOwner && name === APP_ROLES.appCustomerOwner.name);

        if (isRoleToDrop) {
          return null;
        }

        return { label: APP_ROLES[name].title, value: name };
      }).filter(Boolean),
    [args],
  );

  const [eventResolver] = useMutation(EVENT_RESOLVER_MUTATION, {
    refetchQueries: ['UsersTableContent', 'PortfolioCompaniesEntity', 'LoanApplicationsEntity'],
    awaitRefetchQueries: true,
    onError: error => notification.error(commonErrorResolver(error)),
    onCompleted: () => {
      notification.success(t('teammate_invite_success'));

      if (isFunction(args?.onSuccess)) {
        args.onSuccess();
      }

      closeModal();
    },
  });

  const onSubmit = React.useCallback(
    async data => {
      await eventResolver({
        variables: {
          event: data,
        },
      });
    },
    [eventResolver],
  );

  const onClose = React.useCallback(
    (pristine: boolean) => {
      if (!pristine) {
        openModal(DIALOGS.DATA_LOSS_DIALOG, {
          onConfirm: () => {
            closeModal();
          },
        });
      } else {
        closeModal();
      }
    },
    [closeModal, openModal],
  );

  const initialValues = React.useMemo(
    () => ({
      type: EventType.CompanyTeammateInvitationSend,
      payload: {
        companyId: args?.companyId,
        appRoleName: roleOptions[0]?.value,
      },
    }),
    [args, roleOptions],
  );

  if (!open) {
    return null;
  }

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={values => validateDataByYup(CompanyTeammateInvitationSendEventSchema, values)}
    >
      {({ handleSubmit, submitting, pristine }): React.ReactNode => (
        <Dialog
          disableRestoreFocus
          open={open}
          onClose={() => onClose(pristine)}
          PaperProps={{
            className: classes.root,
            component: 'form',
            onSubmit: handleSubmit,
          }}
          maxWidth="md"
        >
          <DialogTitle>Invite Teammate</DialogTitle>

          <DialogContent>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <Field
                  component={TextField}
                  name="payload.firstName"
                  label="First Name*"
                  disabled={submitting}
                  fullWidth
                />
              </Grid>
              <Grid item>
                <Field
                  component={TextField}
                  name="payload.lastName"
                  label="Last Name*"
                  disabled={submitting}
                  fullWidth
                />
              </Grid>
              <Grid item>
                <Field
                  component={TextField}
                  name="payload.email"
                  label="Email*"
                  disabled={submitting}
                  fullWidth
                />
              </Grid>
              <Grid item>
                <Field
                  component={TextField}
                  name="payload.title"
                  label="Title"
                  disabled={submitting}
                  fullWidth
                />
              </Grid>
              <Grid item>
                <Field
                  component={SelectField}
                  name="payload.appRoleName"
                  label="Role*"
                  disabled={submitting}
                  options={roleOptions}
                  fullWidth
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button color="secondary" disabled={submitting} onClick={() => onClose(pristine)}>
              Cancel
            </Button>
            <SubmitButton
              color="secondary"
              variant="contained"
              type="submit"
              disabled={pristine}
              loading={submitting}
            >
              Invite
            </SubmitButton>
          </DialogActions>
        </Dialog>
      )}
    </Form>
  );
};
